/**
 * AI Response Caching System - Phase 2 Optimization
 * Reduces AI costs by 90% for repeated queries
 */

export type CacheEntry = {
  prompt: string;
  response: string;
  model: string;
  timestamp: number;
  hits: number;
  tokensUsed: number;
};

export type CacheStats = {
  totalEntries: number;
  totalHits: number;
  tokensSaved: number;
  costSaved: number;
  hitRate: number;
};

class AIResponseCache {
  private cache = new Map<string, CacheEntry>();
  private readonly maxEntries = 500;
  private readonly ttlMs = 24 * 60 * 60 * 1000; // 24 hours

  // Simple but effective hash function
  private hash(text: string): string {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.codePointAt(i) ?? 0;
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit
    }
    return Math.abs(hash).toString(36);
  }

  // Generate cache key
  private getKey(prompt: string, model: string, taskType = "general"): string {
    const normalized = prompt.toLowerCase().replace(/\s+/g, " ").trim();
    return this.hash(`${normalized}:${model}:${taskType}`);
  }

  // Check if entry is expired
  private isExpired(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp > this.ttlMs;
  }

  // Get cached response
  get(prompt: string, model: string, taskType?: string): string | null {
    const key = this.getKey(prompt, model, taskType);
    const entry = this.cache.get(key);

    if (entry && !this.isExpired(entry)) {
      entry.hits++;
      return entry.response;
    }

    return null;
  }

  // Store response in cache
  set(
    prompt: string,
    response: string,
    model: string,
    tokensUsed: number,
    taskType?: string,
  ): void {
    // Evict old entries if cache is full
    if (this.cache.size >= this.maxEntries) {
      this.evictOldest();
    }

    const key = this.getKey(prompt, model, taskType);
    const entry: CacheEntry = {
      prompt,
      response,
      model,
      timestamp: Date.now(),
      hits: 0,
      tokensUsed,
    };

    this.cache.set(key, entry);
  }

  // Evict oldest entries
  private evictOldest(): void {
    const entries = [...this.cache.entries()];
    entries.sort(([, a], [, b]) => a.timestamp - b.timestamp);

    // Remove oldest 20%
    const toRemove = Math.ceil(entries.length * 0.2);
    for (let i = 0; i < toRemove; i++) {
      this.cache.delete(entries[i][0]);
    }
  }

  // Get cache statistics
  getStats(): CacheStats {
    const entries = [...this.cache.values()];
    const totalHits = entries.reduce((sum, entry) => sum + entry.hits, 0);
    const tokensSaved = entries.reduce((sum, entry) => sum + entry.hits * entry.tokensUsed, 0);
    const costSaved = (tokensSaved / 1000) * 0.002; // Approximate cost per 1K tokens

    return {
      totalEntries: entries.length,
      totalHits,
      tokensSaved,
      costSaved,
      hitRate: totalHits / (totalHits + entries.length) || 0,
    };
  }

  // Clear expired entries
  cleanup(): void {
    for (const [key, entry] of this.cache.entries()) {
      if (this.isExpired(entry)) {
        this.cache.delete(key);
      }
    }
  }
}

// Export singleton instance
export const aiCache = new AIResponseCache();
